import React, { useCallback } from 'react';
import { deleteActiveCodeField } from '../state/actions/delete-active-code-field';
import { deleteItems } from '../state/actions/delete-items';
import {
  deleteTransitions,
  getSelectedTransitionIds,
  getSelectedNonTransitionIds,
} from '../state/actions/delete-transition';
import {
  useCurrentStateAsRef,
  useSelectedItems,
  useWriteContext,
} from '../utils/use-context';
import { useTranslations } from 'next-intl';
import { ToolButton } from '@crabs-ai/ds-react';
import { Delete } from '@crabs-ai/web-icons';

export function DeleteItemTool() {
  const t = useTranslations();
  const { selectedItems } = useSelectedItems();
  const { setState } = useWriteContext();
  const stateAsRef = useCurrentStateAsRef();

  const canDelete = selectedItems.length > 0;

  const handleDelete = useCallback(() => {
    if (!canDelete) return;

    const codeFieldDeleteResult = deleteActiveCodeField(
      stateAsRef.current,
      stateAsRef.current.selectedItems,
    );

    if (codeFieldDeleteResult) {
      if (codeFieldDeleteResult.state === stateAsRef.current) {
        return;
      }

      setState({
        update: codeFieldDeleteResult.state,
        commitToUndoStack: codeFieldDeleteResult.commitToUndoStack,
      });
      return;
    }

    setState({
      update: (state) => {
        const transitionIds = getSelectedTransitionIds(state);
        const nonTransitionIds = getSelectedNonTransitionIds(state);

        const stateTracks = state.undoableState.tracks;
        const deletableItems = nonTransitionIds.filter((itemId) => {
          const track = stateTracks.find((t) => t.items.includes(itemId));
          return !track?.locked;
        });

        if (transitionIds.length === 0 && deletableItems.length === 0) {
          return state;
        }

        let newState = state;
        if (transitionIds.length > 0) {
          newState = deleteTransitions(newState, transitionIds);
        }

        if (deletableItems.length > 0) {
          newState = deleteItems(newState, deletableItems, {
            isLinkingEnabled: state.isLinkingEnabled,
          });
        }

        return newState;
      },
      commitToUndoStack: true,
    });
  }, [canDelete, setState, stateAsRef]);

  return (
    <ToolButton
      size="sm"
      isTransparent={false}
      state={!canDelete ? 'disabled' : 'default'}
      onClick={handleDelete}
      disabled={!canDelete}
      isShowTooltip
      tooltipNode={t('Editor.timeline-controls.delete')}
      tooltipSubNode="[Delete]"
      tooltipPosition="top"
    >
      <Delete className="size-5" />
    </ToolButton>
  );
}
